import { z } from 'zod'
import { paginatedQuery, PAGE_NEEDS_LIMIT } from './pagination.input.js'
import { primitiveSchema } from '../dto/primitive.dto.js'
import { furnitureClassSchema, type FurnitureClass } from '../enums/furniture-class.enum.js'
import { furnitureTagFilterSchema, furnitureTagsSchema } from '../helpers/furniture-tag.js'
import { FURNITURE_MAX_CAPACITY_MAX } from '../helpers/furniture-capacity.js'
import { danceFloorFeetSchema, danceFloorUpdateIssue, settleDanceFloor } from '../helpers/dance-floor.js'
import { riserFeetSchema, riserUpdateIssue, settleRiser } from '../helpers/riser.js'
import { imageAssetRefSchema } from '../helpers/asset-ref.js'

/**
 * A furniture thumbnail write value: storage key (after upload), external
 * http(s) URL, or inline data URL when object storage is off.
 */
export const furnitureImageSchema = imageAssetRefSchema
export type FurnitureImageInput = z.infer<typeof furnitureImageSchema>

/**
 * How many chairs a table seats. Only a table has one — the API clears it from
 * anything else rather than refusing the write, because a piece reclassified
 * from Table to Other is a correction, not a mistake to argue with.
 */
export const furnitureMaxCapacitySchema = z
  .number()
  .int()
  .positive('Max capacity must be at least 1')
  .max(FURNITURE_MAX_CAPACITY_MAX, `Max capacity cannot exceed ${FURNITURE_MAX_CAPACITY_MAX}`)

/** Create a catalog furniture item. Its shape (primitives) can be designed now
 * or left empty and drawn later in the furniture editor. A dance floor and a
 * riser are the exceptions: neither is drawn, and their two size fields are
 * what each is. */
const createFurnitureItemObjectSchema = z
  .object({
    name: z.string().trim().min(1, 'Name is required').max(120),
    // Broad category; defaults to Other when the caller doesn't classify it.
    class: furnitureClassSchema.default('Other'),
    primitives: z.array(primitiveSchema).default([]),
    // Optional thumbnail; omit or null for none.
    imageUrl: furnitureImageSchema.nullish(),
    /**
     * The venue that owns this piece. Required, and not only by convention: the
     * database refuses a catalog row naming no owner, so accepting one here
     * would turn a missing field into a 500 instead of a 400.
     */
    venueId: z.string().uuid(),
    /** What the venue calls this piece. Omit to leave it for someone to say later. */
    tags: furnitureTagsSchema.optional(),
    // Stock count for a vendor's own item; ignored for tenant catalog items.
    quantity: z.number().int().nonnegative().nullish(),
    // Per-unit price in whole cents; optional catalog metadata.
    price: z.number().int().nonnegative().nullish(),
    // Short freeform note (material, color, etc.).
    description: z.string().trim().max(500).nullish(),
    /** Seats at this table; omit or null for a piece that seats nobody. */
    maxCapacity: furnitureMaxCapacitySchema.nullish(),
    /** How big a new dance floor is, in feet. Required together with the increment when the class is DanceFloor. */
    danceFloorDefaultFeet: danceFloorFeetSchema.nullish(),
    /** The step, in feet, by which a placed dance floor may grow. Both sides stay equal. */
    danceFloorIncrementFeet: danceFloorFeetSchema.nullish(),
    /** How wide one riser unit is, in feet. Required together with its length when the class is Riser. */
    riserUnitWidthFeet: riserFeetSchema.nullish(),
    /** How long one riser unit is, in feet. A placed runway is a whole number of these. */
    riserUnitLengthFeet: riserFeetSchema.nullish(),
    /**
     * The key an importer knows this piece by. With one, the create is
     * idempotent for the venue: the piece it made last time comes back instead
     * of a second copy, whatever a person has since renamed or re-described it.
     */
    importKey: z.string().trim().min(1).max(200).optional(),
  })
  .strict()

/**
 * The checks a create shares with the vendor's create: a dance floor needs its
 * size and step, and a riser its unit, before either can be placed.
 */
function refineMeasuredPieces(
  value: {
    class: FurnitureClass
    danceFloorDefaultFeet?: number | null | undefined
    danceFloorIncrementFeet?: number | null | undefined
    riserUnitWidthFeet?: number | null | undefined
    riserUnitLengthFeet?: number | null | undefined
  },
  ctx: z.RefinementCtx,
): void {
  const danceFloor = settleDanceFloor(value.class, value.danceFloorDefaultFeet ?? null, value.danceFloorIncrementFeet ?? null)
  if (!danceFloor.ok) ctx.addIssue({ code: 'custom', message: danceFloor.message, path: ['danceFloorDefaultFeet'] })
  const riser = settleRiser(value.class, value.riserUnitWidthFeet ?? null, value.riserUnitLengthFeet ?? null)
  if (!riser.ok) ctx.addIssue({ code: 'custom', message: riser.message, path: ['riserUnitWidthFeet'] })
}

export const createFurnitureItemInputSchema = createFurnitureItemObjectSchema.superRefine(refineMeasuredPieces)

export type CreateFurnitureItemInput = z.infer<typeof createFurnitureItemInputSchema>

/**
 * A vendor's own piece, which has no venue and never will.
 *
 * Its own schema because the venue one requires a venue: a vendor library is
 * reached through the vendor's venue links, not by belonging anywhere, and the
 * database says so — of vendor, event and venue, exactly one owns a piece.
 */
export const createVendorFurnitureItemInputSchema = createFurnitureItemObjectSchema
  .omit({ venueId: true })
  .superRefine(refineMeasuredPieces)

export type CreateVendorFurnitureItemInput = z.infer<typeof createVendorFurnitureItemInputSchema>

/** Update a catalog furniture item — rename it, replace its designed shape, or
 * move it to another venue. Every field is optional; omitted means unchanged. */
export const updateFurnitureItemInputSchema = z
  .object({
    name: z.string().trim().min(1, 'Name is required').max(120).optional(),
    class: furnitureClassSchema.optional(),
    primitives: z.array(primitiveSchema).optional(),
    // omit to leave unchanged, null to remove the thumbnail, or a new image.
    imageUrl: furnitureImageSchema.nullable().optional(),
    /** The venue that owns this piece. */
    venueId: z.string().uuid().optional(),
    quantity: z.number().int().nonnegative().nullable().optional(),
    price: z.number().int().nonnegative().nullable().optional(),
    description: z.string().trim().max(500).nullable().optional(),
    /** Omit to leave unchanged, null to clear the seat count. */
    maxCapacity: furnitureMaxCapacitySchema.nullable().optional(),
    /** Omit to leave unchanged. Null is not a dance floor; the service clears both when the class is anything else. */
    danceFloorDefaultFeet: danceFloorFeetSchema.nullable().optional(),
    danceFloorIncrementFeet: danceFloorFeetSchema.nullable().optional(),
    /** Omit to leave unchanged. Null is not a riser; the service clears both when the class is anything else. */
    riserUnitWidthFeet: riserFeetSchema.nullable().optional(),
    riserUnitLengthFeet: riserFeetSchema.nullable().optional(),
    /** Omit to leave unchanged; an empty list to put it back to untagged. */
    tags: furnitureTagsSchema.optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    const message = danceFloorUpdateIssue(value.class, value.danceFloorDefaultFeet, value.danceFloorIncrementFeet)
    if (message !== null) ctx.addIssue({ code: 'custom', message, path: ['danceFloorDefaultFeet'] })
    const riser = riserUpdateIssue(value.class, value.riserUnitWidthFeet, value.riserUnitLengthFeet)
    if (riser !== null) ctx.addIssue({ code: 'custom', message: riser, path: ['riserUnitWidthFeet'] })
  })

export type UpdateFurnitureItemInput = z.infer<typeof updateFurnitureItemInputSchema>

/**
 * What a furniture listing is asking for.
 *
 * `venueId` is the whole of it: furniture belongs to a venue, so the page that
 * manages it names the venue being worked in. Omitting it returns the
 * organization's whole catalog, which the gallery and the importer want and the
 * furniture page never does.
 */
export const furnitureListQuerySchema = paginatedQuery({
  venueId: z.string().uuid().optional(),
  /**
   * Narrow to the pieces carrying *all* of these tags — each one asked for
   * leaves less, which is what narrowing a list means. Repeatable:
   * `?tag=a&tag=b`. For the pieces carrying none, ask `untagged=true` instead;
   * the two cannot be combined, because nothing carries a tag and no tags at
   * once. Filtered in the query rather than on screen because the list is
   * paged: filtering a page after it arrives would count and paginate the
   * unfiltered set and show a page with three rows on it.
   */
  tag: furnitureTagFilterSchema.optional(),
  /**
   * The pieces carrying no tags at all. Explicit `'true'`/`'false'` rather than
   * a coerced boolean, for the reason `prismCatalogQuerySchema` gives: a query
   * string carries only strings, and `Boolean('false')` is `true`.
   */
  untagged: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
}).refine(...PAGE_NEEDS_LIMIT)

export type FurnitureListQuery = z.infer<typeof furnitureListQuerySchema>
